import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Divider, 
  Avatar, 
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  CircularProgress,
  IconButton,
  Tooltip,
  Button
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaymentsIcon from '@mui/icons-material/Payments';
import { useNavigate } from 'react-router-dom';
//import useNavigationHelperStore from '../../store/navigationHelperStore';
import axios from 'axios';
import { useSnackbar } from '../../providers/SnackBarProvider';
import { handleError, formatCurrency } from '../../utils/lib';
import { useTranslation } from 'react-i18next';

const ListItemOwnerDebtor = ({ owner, onAddPayment }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  //const { setNavigation } = useNavigationHelperStore();
  const snackbar = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [paymentDialog, setPaymentDialog] = useState({
    open: false,
    amount: '',
    paymentType: null
  });

  useEffect(() => {
    if (paymentDialog.open) {
      loadPaymentTypes();
    }
  }, [paymentDialog.open]);

  const loadPaymentTypes = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}finance?payment_types`,
        { withCredentials: true }
      );
      const paymentTypesList = response.data.data;
      setPaymentTypes(paymentTypesList);
      
      // Find and set "Efectivo" as default payment type
      const cashPaymentType = paymentTypesList.find(type => type.name.toLowerCase() === 'efectivo') || null;
      if (cashPaymentType) {
        setPaymentDialog(prev => ({
          ...prev,
          paymentType: cashPaymentType
        }));
      }
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewDetails = () => {
    // Save current state before navigating
    // if (onSaveCurrentState) {
    //   onSaveCurrentState();
    // }
    // Navigate to the owner details view
    navigate('/main/view-owner', { 
      state: { 
        id: owner.id
      } 
    });
  };

  const handleViewDebtDetail = () => {
    // Save current state before navigating
    // if (onSaveCurrentState) {
    //   onSaveCurrentState();
    // }
    // Navigate to the owner debts view
    navigate('/main/view-owner-debts', { 
      state: { 
        id: owner.id,
        name: owner.name,
        thumb_url: owner.thumb_url
      } 
    });
  };

  const handleOpenPaymentDialog = () => {
    setPaymentDialog({
      open: true,
      amount: owner.balance?.toString() || '',
      paymentType: null
    });
  };

  const handleClosePaymentDialog = () => {
    setPaymentDialog({
      open: false,
      amount: '',
      paymentType: null
    });
  };

  const handleAmountChange = (event) => {
    setPaymentDialog(prev => ({
      ...prev,
      amount: event.target.value
    }));
  };

  const handlePaymentTypeChange = (event, newValue) => {
    setPaymentDialog(prev => ({
      ...prev,
      paymentType: newValue
    }));
  };

  const handleSubmitPayment = async () => {
    try {
      if (!paymentDialog.paymentType) {
        snackbar.showSnackbar(t('reports.pleaseSelectPaymentMethod'), "error");
        return;
      }

      if (!paymentDialog.amount || parseFloat(paymentDialog.amount) <= 0) {
        snackbar.showSnackbar(t('reports.pleaseEnterValidAmount'), "error");
        return;
      }

      setIsLoading(true);
      
      // Send payment to API
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}sells_payments`,
        {
          owner: {
            id: owner.id 
          },
          finance_payment_method: {
            id: paymentDialog.paymentType.id
          },
          amount: paymentDialog.amount,
        },
        { withCredentials: true }
      );

      if (response.data.status === 'SUCCESS') {
        handleClosePaymentDialog();
        onAddPayment();
        snackbar(t('reports.paymentRegisteredSuccess'));
      }
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Paper elevation={0} sx={{ mb: 1 }}>
        <Box sx={{ display: 'flex', p: 1 }}>
          {/* Left side with avatar and owner info */}
          <Box sx={{ display: 'flex', flex: 1, cursor: 'pointer' }} onClick={handleViewDetails}>
            <Avatar 
              sx={{ width: 42, height: 42, mr: 1 }}
              alt={owner.name}
              src={owner.thumb_url}
            >
              {!owner.thumb_url && <PersonIcon />}
            </Avatar>
            
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                {owner.name}
              </Typography>
              
              <Typography variant="caption" noWrap sx={{ display: 'block' }}>
                {owner.email}
              </Typography>
              
              <Typography variant="caption" sx={{ display: 'block' }}>
                {t('common.balance')}: {formatCurrency(owner.balance)}
              </Typography>
            </Box>
          </Box>
          
          {/* Right side with action buttons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title={t('reports.debtDetail')}>
              <IconButton 
                size="small" 
                onClick={handleViewDebtDetail}
              >
                <ReceiptIcon />
              </IconButton>
            </Tooltip>
            
            <Tooltip title={t('reports.registerPayment')}>
              <IconButton 
                size="small" 
                onClick={handleOpenPaymentDialog}
              >
                <PaymentsIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        
        <Divider sx={{ mx: 1 }} />
      </Paper>

      {/* Payment Dialog */}
      <Dialog
        open={paymentDialog.open}
        onClose={handleClosePaymentDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('reports.registerPayment')}</DialogTitle>
        <DialogContent>
          {isLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress size={30} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <Typography variant="subtitle1">
                {t('reports.clientLabel')} {owner.name}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t('view.currentDebt')} {formatCurrency(owner.balance)}
              </Typography>
              
              <Autocomplete
                fullWidth
                options={paymentTypes}
                getOptionLabel={(option) => option?.name || ""}
                value={paymentDialog.paymentType}
                onChange={handlePaymentTypeChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('common.paymentMethod')}
                    required
                  />
                )}
              />
              
              <TextField
                label={t('reports.amountToPay')}
                type="number"
                value={paymentDialog.amount}
                onChange={handleAmountChange}
                fullWidth
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                }}
                variant="outlined"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleClosePaymentDialog}
            disabled={isLoading}
          >
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleSubmitPayment} 
            variant="contained" 
            color="primary"
            disabled={isLoading || !paymentDialog.paymentType || !paymentDialog.amount}
          >
            {t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ListItemOwnerDebtor; 